import { createSimulation } from './simulation.ts';
import type { SimulationConfig, SystemType } from './types.ts';
import type { Marker, MarkerType } from './clipper.ts';

export type { Marker, MarkerType };

export interface SAOptions {
  initialTemp: number;
  coolingRate: number;
  iterations: number;
  perturbScale: number;
  windowRadius: number;
}

export interface OptimizerResult {
  state: number[];
  delta: number;
  iterations: number;
  targetFrame: number;
  targetType: 'full' | 'half';
}

function runSimulationFrames(
  config: SimulationConfig,
  initialState: number[],
  frames: number,
): number[][] {
  const sim = createSimulation(config.system, config.params, config.sim, initialState);
  const states: number[][] = [];
  for (let f = 0; f < frames; f++) {
    sim.step();
    states.push(sim.getState());
  }
  return states;
}

function mirrorState(_system: SystemType, state: number[]): number[] {
  return state.map((v) => -v);
}

function evaluateDelta(
  config: SimulationConfig,
  marker: Marker,
  initialState: number[],
  windowRadius: number,
): number {
  const targetFrame = marker.frame;
  const frames = targetFrame + windowRadius + 1;
  const states = runSimulationFrames(config, initialState, frames);
  if (states.length === 0) return Infinity;

  const startIdx = Math.max(0, targetFrame - windowRadius);
  const endIdx = Math.min(states.length - 1, targetFrame + windowRadius);
  const windowStates = states.slice(startIdx, endIdx + 1);
  if (windowStates.length === 0) return Infinity;

  const target = marker.type === 'half'
    ? mirrorState(config.system, initialState)
    : initialState;
  const dim = target.length;

  // Normalise by the standard deviation of each phase-space coordinate over the
  // comparison window, so no single coordinate dominates the distance.
  const stds: number[] = [];
  for (let d = 0; d < dim; d++) {
    let mean = 0;
    for (const s of windowStates) mean += s[d];
    mean /= windowStates.length;
    let v = 0;
    for (const s of windowStates) {
      const diff = s[d] - mean;
      v += diff * diff;
    }
    stds[d] = Math.sqrt(v / windowStates.length) || 1;
  }

  let bestDelta = Infinity;
  for (const s of windowStates) {
    let delta = 0;
    for (let d = 0; d < dim; d++) {
      const diff = (s[d] - target[d]) / stds[d];
      delta += diff * diff;
    }
    if (delta < bestDelta) bestDelta = delta;
  }
  return bestDelta;
}

function perturbState(state: number[], scale: number): number[] {
  return state.map((v) => v + (Math.random() - 0.5) * 2 * scale);
}

function adaptiveScale(baseScale: number, iter: number, total: number): number {
  // shrink the proposal distribution as the annealing progresses
  const frac = iter / Math.max(1, total);
  return baseScale * (1 - 0.7 * frac);
}

export function runSimulatedAnnealing(
  config: SimulationConfig,
  marker: Marker,
  initialState: number[],
  options: SAOptions,
  onProgress?: (iter: number, bestDelta: number, currentDelta: number) => void,
): Promise<OptimizerResult> {
  return new Promise((resolve) => {
    let current = initialState.slice();
    let currentEnergy = evaluateDelta(config, marker, current, options.windowRadius);
    let best = current.slice();
    let bestEnergy = currentEnergy;
    let T = options.initialTemp;
    let iter = 0;

    function step() {
      if (iter >= options.iterations || !isFinite(currentEnergy)) {
        resolve({
          state: best,
          delta: bestEnergy,
          iterations: iter,
          targetFrame: marker.frame,
          targetType: marker.type as 'full' | 'half',
        });
        return;
      }

      const candidate = perturbState(current, adaptiveScale(options.perturbScale, iter, options.iterations));
      const candidateEnergy = evaluateDelta(config, marker, candidate, options.windowRadius);
      const deltaE = candidateEnergy - currentEnergy;

      if (deltaE < 0 || (isFinite(deltaE) && Math.random() < Math.exp(-deltaE / T))) {
        current = candidate;
        currentEnergy = candidateEnergy;
        if (candidateEnergy < bestEnergy) {
          best = candidate.slice();
          bestEnergy = candidateEnergy;
        }
      }
      T *= options.coolingRate;
      iter++;

      if (iter % 10 === 0) {
        onProgress?.(iter, bestEnergy, currentEnergy);
      }

      setTimeout(step, 0);
    }

    step();
  });
}

export function runGreedyDescent(
  config: SimulationConfig,
  marker: Marker,
  initialState: number[],
  options: SAOptions,
  onProgress?: (iter: number, bestDelta: number, currentDelta: number) => void,
): Promise<OptimizerResult> {
  return new Promise((resolve) => {
    let current = initialState.slice();
    let currentEnergy = evaluateDelta(config, marker, current, options.windowRadius);
    let best = current.slice();
    let bestEnergy = currentEnergy;
    let stale = 0;
    const maxStale = 200;
    let iter = 0;
    let scale = options.perturbScale;

    function step() {
      if (iter >= options.iterations || !isFinite(currentEnergy)) {
        resolve({
          state: best,
          delta: bestEnergy,
          iterations: iter,
          targetFrame: marker.frame,
          targetType: marker.type as 'full' | 'half',
        });
        return;
      }

      const candidate = perturbState(current, scale);
      const candidateEnergy = evaluateDelta(config, marker, candidate, options.windowRadius);

      if (candidateEnergy < currentEnergy) {
        current = candidate;
        currentEnergy = candidateEnergy;
        stale = 0;
        if (candidateEnergy < bestEnergy) {
          best = candidate.slice();
          bestEnergy = candidateEnergy;
        }
      } else {
        stale++;
        if (stale >= maxStale) {
          scale *= 0.5;
          stale = 0;
        }
      }

      iter++;
      if (iter % 10 === 0) {
        onProgress?.(iter, bestEnergy, currentEnergy);
      }

      setTimeout(step, 0);
    }

    step();
  });
}

export type OptimizerMethod = 'sa' | 'greedy';

export interface OptimizerPanel {
  setAvailable(hasFull: boolean, hasHalf: boolean): void;
  setRunning(running: boolean): void;
  setResult(result: OptimizerResult | null): void;
  setProgress(iter: number, total: number, delta: number): void;
  clear(): void;
}

interface OptimizerPanelOpts {
  methodSelect: HTMLSelectElement;
  targetSelect: HTMLSelectElement;
  runBtn: HTMLButtonElement;
  loadBtn: HTMLButtonElement;
  tempSlider: HTMLInputElement;
  tempDisplay: HTMLElement;
  coolSlider: HTMLInputElement;
  coolDisplay: HTMLElement;
  iterInput: HTMLInputElement;
  perturbSlider: HTMLInputElement;
  perturbDisplay: HTMLElement;
  info: HTMLElement;
  resultPre: HTMLElement;
  getConfig: () => SimulationConfig | null;
  getMarker: (type: MarkerType) => Marker | null;
  getInitialState: () => number[];
  onLoad: (state: number[]) => void;
}

export function createOptimizerPanel(opts: OptimizerPanelOpts): OptimizerPanel {
  const {
    methodSelect, targetSelect, runBtn, loadBtn,
    tempSlider, tempDisplay, coolSlider, coolDisplay,
    iterInput, perturbSlider, perturbDisplay,
    info, resultPre,
    getConfig, getMarker, getInitialState, onLoad,
  } = opts;

  let lastResult: OptimizerResult | null = null;
  let running = false;

  function updateDisplays() {
    tempDisplay.textContent = parseFloat(tempSlider.value).toFixed(3);
    coolDisplay.textContent = parseFloat(coolSlider.value).toFixed(4);
    perturbDisplay.textContent = parseFloat(perturbSlider.value).toFixed(3);
    const isSA = methodSelect.value === 'sa';
    tempSlider.parentElement!.style.display = isSA ? 'flex' : 'none';
    coolSlider.parentElement!.style.display = isSA ? 'flex' : 'none';
  }

  function formatState(state: number[]): string {
    return [
      `θ₁ ${state[0].toFixed(6)}`,
      `ω₁ ${state[1].toFixed(6)}`,
      state[2] !== undefined ? `r₁ ${state[2].toFixed(6)}` : '',
      state[3] !== undefined ? `ṙ₁ ${state[3].toFixed(6)}` : '',
      `θ₂ ${state[4].toFixed(6)}`,
      `ω₂ ${state[5].toFixed(6)}`,
      state[6] !== undefined ? `r₂ ${state[6].toFixed(6)}` : '',
      state[7] !== undefined ? `ṙ₂ ${state[7].toFixed(6)}` : '',
    ].filter(Boolean).join('   ');
  }

  function readOptions(): SAOptions {
    return {
      initialTemp: Math.max(1e-6, parseFloat(tempSlider.value) || 0.1),
      coolingRate: Math.max(0.5, Math.min(0.99999, parseFloat(coolSlider.value) || 0.995)),
      iterations: Math.max(50, Math.floor(parseInt(iterInput.value) || 500)),
      perturbScale: Math.max(1e-6, parseFloat(perturbSlider.value) || 0.05),
      windowRadius: 50,
    };
  }

  async function run() {
    const config = getConfig();
    if (!config) {
      info.textContent = 'No simulation config available.';
      return;
    }
    const targetType = targetSelect.value as 'full' | 'half';
    const marker = getMarker(targetType);
    if (!marker) {
      info.textContent = `No ${targetType} orbit marker. Label an orbit first.`;
      return;
    }

    const method = methodSelect.value as OptimizerMethod;
    const options = readOptions();
    const initial = getInitialState();
    running = true;
    runBtn.disabled = true;
    loadBtn.disabled = true;
    resultPre.textContent = '';
    const methodName = method === 'sa' ? 'Simulated Annealing' : 'Greedy Descent';
    info.textContent = `Running ${methodName}: ${options.iterations} iterations for ${targetType} orbit (frame ${marker.frame})...`;

    try {
      const runner = method === 'sa' ? runSimulatedAnnealing : runGreedyDescent;
      const result = await runner(
        config,
        marker,
        initial,
        options,
        (iter, bestDelta, currentDelta) => {
          info.textContent = `${methodName} iter ${iter} / ${options.iterations} · best δ ${bestDelta.toFixed(6)} · current δ ${currentDelta.toFixed(6)}`;
        },
      );
      lastResult = result;
      info.textContent = `${methodName} finished ${result.iterations} iterations · best δ ${result.delta.toFixed(6)}`;
      resultPre.textContent = `method: ${methodName}\ntarget: ${result.targetType} orbit at frame ${result.targetFrame}\ninitial: ${formatState(initial)}\nbest:    ${formatState(result.state)}\nδ       ${result.delta.toFixed(8)}`;
    } catch (err) {
      console.error('optimizer error:', err);
      info.textContent = 'Optimizer failed. See console for details.';
    } finally {
      running = false;
      runBtn.disabled = false;
      loadBtn.disabled = !lastResult;
    }
  }

  runBtn.addEventListener('click', run);

  loadBtn.addEventListener('click', () => {
    if (!lastResult) return;
    onLoad(lastResult.state);
    info.textContent = 'Loaded optimized initial state into simulation.';
  });

  [tempSlider, coolSlider, perturbSlider].forEach((el) => {
    el.addEventListener('input', updateDisplays);
  });
  methodSelect.addEventListener('change', updateDisplays);

  updateDisplays();
  loadBtn.disabled = true;

  return {
    setAvailable(hasFull: boolean, hasHalf: boolean): void {
      const options = targetSelect.querySelectorAll('option');
      options[0].disabled = !hasFull;
      options[1].disabled = !hasHalf;
      if (targetSelect.value === 'full' && !hasFull) targetSelect.value = 'half';
      if (targetSelect.value === 'half' && !hasHalf) targetSelect.value = 'full';
      if (!hasFull && !hasHalf) {
        info.textContent = 'Label an orbit to enable optimization.';
      }
    },
    setRunning(r: boolean): void {
      running = r;
      runBtn.disabled = r;
      loadBtn.disabled = r || !lastResult;
    },
    setResult(result: OptimizerResult | null): void {
      lastResult = result;
      if (!result) {
        resultPre.textContent = '';
        loadBtn.disabled = true;
        return;
      }
      resultPre.textContent = `target: ${result.targetType} orbit at frame ${result.targetFrame}\ninitial: ${formatState(getInitialState())}\nbest:    ${formatState(result.state)}\nδ       ${result.delta.toFixed(8)}`;
      loadBtn.disabled = false;
    },
    setProgress(iter: number, total: number, delta: number): void {
      info.textContent = `iter ${iter} / ${total} · best δ ${delta.toFixed(6)}`;
    },
    clear(): void {
      lastResult = null;
      resultPre.textContent = '';
      info.textContent = '';
      loadBtn.disabled = true;
      updateDisplays();
    },
  };
}
